<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'UcContractLifecycle',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    contract: {
      type: Object,
      required: true,
    },
    readonly: {
      type: Boolean,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const labelCols = ref(6);
  const ux = ref([]);

  const $data = computed(() => ({
    labelCols: labelCols.value,
    ux: ux.value,
  }));

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [{ name: 'labelCols' }, { name: 'ux' }],
    loaders: {},
    ux: {},
    dataRefs: { labelCols, ux },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const item = computed(() => {
    return props.contract.lifecycle;
  });

  watch(
    () => props.contract,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('contract');
    }
  );
  watch(
    () => props.readonly,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('readonly');
    }
  );

  const act_2095089031469157_emit_1eleesxby_getActionArgs = async ($event) => {
    return {
      event: 'reload',
    };
  };
  const inst_evh_2095089031469157_close_46bzixvxq_getWhen = async ($event) => {
    return !!$event.data.modalResult.value;
  };
  const act_2095089031469157_showModal_bs88ezaa3_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'ContractEditor',
      props: {
        target: 'lifecycle',
        contract: _.cloneDeep(props.contract),
      },
    };
  };

  const act_7315092382398562_reloadComponentData_fszzttza0 =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_o5hc9qau8 =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_fszzttza0,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_o5hc9qau8],
    event: 'reload',
  });
  const act_2095089031469157_emit_1eleesxby = new actionTypes.EmitAction({
    actionArgs: act_2095089031469157_emit_1eleesxby_getActionArgs,
  });
  const inst_evh_2095089031469157_close_46bzixvxq =
    new actionTypes.EventHandlerImpl({
      action: act_2095089031469157_emit_1eleesxby,
      event: 'close',
      when: inst_evh_2095089031469157_close_46bzixvxq_getWhen,
      displayName: 'emit',
    });
  const act_2095089031469157_showModal_bs88ezaa3 =
    new actionTypes.ShowModalAction({
      actionArgs: act_2095089031469157_showModal_bs88ezaa3_getActionArgs,
      events: [inst_evh_2095089031469157_close_46bzixvxq],
    });
  const inst_evh_2095089031469157_clickPreventStop_nqx2y63s3 =
    new actionTypes.EventHandlerImpl({
      action: act_2095089031469157_showModal_bs88ezaa3,
      event: 'click.prevent.stop',
      displayName: 'showModal',
    });
  const inst_evh_2095089031469157_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2095089031469157_clickPreventStop_nqx2y63s3],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_reload_o5hc9qau8,
    inst_evh_7315092382398562_reload,
    inst_evh_2095089031469157_close_46bzixvxq,
    inst_evh_2095089031469157_clickPreventStop_nqx2y63s3,
    inst_evh_2095089031469157_clickPreventStop,
  };

  const evh_7315092382398562_reload_o5hc9qau8 = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_o5hc9qau8.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2095089031469157_close_46bzixvxq = async (event, scope) => {
    return await actions.inst_evh_2095089031469157_close_46bzixvxq.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2095089031469157_clickPreventStop_nqx2y63s3 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2095089031469157_clickPreventStop_nqx2y63s3.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2095089031469157_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2095089031469157_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpUsercontrol
    :loading="loading"
    :reloading="reloading"
    :loaded="loaded"
    :error="error"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <ItpWidgetCard>
      <template #header>
        <ItpBox alignItems="center">
          <strong>
            {{ pgettext('Contract', 'Lifecycle') }}
          </strong>
          <ItpButton
            :text="gettext('Edit...')"
            variant="light"
            size="sm"
            class="ms-auto"
            v-if="!readonly"
            @click.prevent.stop="
              evh_2095089031469157_clickPreventStop($event, {})
            "
          >
          </ItpButton>
        </ItpBox>
      </template>
      <BListGroup v-if="loaded">
        <ItpLabeledListGroupItem
          :label="pgettext('Contract', 'Contract date')"
          :labelCols="labelCols"
          :content="date(item.revision_date)"
          v-if="contract.revision_number == 0 && item.revision_date"
        >
        </ItpLabeledListGroupItem>
        <ItpLabeledListGroupItem
          :label="pgettext('Contract', 'Revision date')"
          :labelCols="labelCols"
          :content="date(item.revision_date)"
          v-if="contract.revision_number > 0 && item.revision_date"
        >
        </ItpLabeledListGroupItem>
        <ItpLabeledListGroupItem
          :label="pgettext('Contract', 'Validity')"
          :labelCols="labelCols"
          v-if="item.valid_from || item.valid_to"
        >
          <UcValidityPeriod
            :from="item.valid_from"
            :to="item.valid_to"
            :missingToMessage="pgettext('ContractValidity', 'Indefinite')"
          >
          </UcValidityPeriod>
        </ItpLabeledListGroupItem>
        <ItpLabeledListGroupItem
          :label="pgettext('Contract', 'Date signed')"
          :labelCols="labelCols"
          v-if="item.date_signed"
        >
          <ItpText>
            {{ date(item.date_signed) }}
          </ItpText>
        </ItpLabeledListGroupItem>
        <ItpLabeledListGroupItem
          :label="pgettext('Contract', 'Effective date')"
          :labelCols="labelCols"
          v-if="item.effective_date"
        >
          <ItpText>
            {{ date(item.effective_date) }}
          </ItpText>
        </ItpLabeledListGroupItem>
        <ItpLabeledListGroupItem
          :label="pgettext('Contract', 'Date of occupancy')"
          :labelCols="labelCols"
          v-if="item.date_of_occupancy"
        >
          <ItpText>
            {{ date(item.date_of_occupancy) }}
          </ItpText>
        </ItpLabeledListGroupItem>
        <ItpLabeledListGroupItem
          :label="pgettext('Contract', 'Auto renewal')"
          :labelCols="labelCols"
          v-if="item.auto_renewal != null"
        >
          <ItpText>
            {{ item.auto_renewal ? gettext('On') : gettext('Off') }}
          </ItpText>
        </ItpLabeledListGroupItem>
        <ItpLabeledListGroupItem
          :label="pgettext('Contract', 'Notice period')"
          :labelCols="labelCols"
          v-if="item.notice_period != null"
        >
          <ItpText>
            {{ item.notice_period }}
          </ItpText>
          <ItpText :text="pgettext('Contract/NoticePeriod', 'day(s)')">
          </ItpText>
        </ItpLabeledListGroupItem>
        <ItpLabeledListGroupItem
          :label="pgettext('Contract', 'Indexing rule')"
          :labelCols="labelCols"
          :content="pgettext('IndexingRule', item.indexing_rule, 'Sentence')"
          v-if="item.indexing_rule"
        >
        </ItpLabeledListGroupItem>
        <ItpLabeledListGroupItem
          :label="pgettext('Contract', 'Next indexation date')"
          :labelCols="labelCols"
          :content="date(item.next_indexation_date)"
          v-if="item.next_indexation_date"
        >
        </ItpLabeledListGroupItem>
        <ItpLabeledListGroupItem
          :label="pgettext('Contract', 'Terminaton date')"
          :labelCols="labelCols"
          :content="date(item.termination_date)"
          v-if="item.termination_date"
        >
        </ItpLabeledListGroupItem>
        <ItpLabeledListGroupItem
          :label="pgettext('Contract', 'Terminaton effective date')"
          :labelCols="labelCols"
          :content="date(item.termination_effective_date)"
          v-if="item.termination_effective_date"
        >
        </ItpLabeledListGroupItem>
      </BListGroup>
    </ItpWidgetCard>
  </ItpUsercontrol>
</template>
